import React, { useCallback, useEffect, useRef, useState } from 'react';

import { authenticatedFetch } from '../utils';
import { audioAvailabilityCache } from '../audioCache';
import { EMPTY, fmtDuration } from '../lib/format';
import { Button, IconButton } from './ui/Button';
import { SegmentedControl } from './ui/Controls';
import { Spinner } from './ui/Skeleton';

/**
 * The call recording player.
 *
 * ONE ELEMENT, APP-WIDE
 * ---------------------
 * Every previous player rendered its own `<audio>`, so opening a second call
 * while the first was playing produced two voices at once — and the paused row
 * still showed a play icon, because nothing told it that it had lost the floor.
 * There is now exactly one element, created lazily at module scope, and exactly
 * one player instance bound to it at a time. Claiming it releases the previous
 * owner, which resets its own transport UI, so the control you are looking at
 * always describes the audio you are hearing.
 *
 * NO AUTOPLAY
 * -----------
 * The signed URL is fetched as soon as we know a recording exists, and the
 * element is pointed at it (metadata only) when nothing else is using it — that
 * is what makes the duration and the scrub bar live before the first click. That
 * is also exactly why `play()` appears nowhere outside a click or key handler:
 * a URL resolving is not consent to make noise, and this component is mounted
 * inside a drawer that a reviewer opens dozens of times an hour.
 */

/** Signed URLs are minted for an hour; re-mint just before the edge. */
const URL_TTL_MINUTES = 60;
const URL_STALE_MS = 55 * 60 * 1000;

const SKIP_SECONDS = 10;

// Four is the ceiling for a segmented control. 2x is the top because speech past
// that is not reviewable, and there is no 0.75x: nobody slows down a phone call.
const SPEEDS = [
  { value: 1, label: '1x' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 2, label: '2x' },
];

/* ─── The shared element ─────────────────────────────────────────────────── */

let sharedEl = null;

/** Identity token of the player currently bound to `sharedEl`, or null. */
let sharedOwner = null;

/** token -> reset its transport UI, called when it loses the element. */
const releaseHandlers = new Map();

const sharedAudio = () => {
  if (!sharedEl) {
    sharedEl = document.createElement('audio');
    // Metadata only: we want the duration without pulling several MB of audio
    // for a call the reviewer may never play.
    sharedEl.preload = 'metadata';
    sharedEl.autoplay = false;
    // Deliberately never appended to the DOM: it has no controls of its own, and
    // an off-screen focusable media element is a phantom tab stop.
  }
  return sharedEl;
};

/** Detach the element from whoever holds it and stop it dead. */
const stopShared = () => {
  sharedOwner = null;
  if (!sharedEl) return;
  sharedEl.pause();
  // Dropping the source cancels an in-flight range request as well as playback;
  // a paused element with a src keeps buffering.
  sharedEl.removeAttribute('src');
  sharedEl.load();
};

/**
 * Bind the element to `token`, releasing the previous owner first.
 * Returns the element, already silent and sourceless if ownership changed.
 */
const claimShared = (token) => {
  const el = sharedAudio();
  if (sharedOwner === token) return el;

  const previous = sharedOwner;
  sharedOwner = token;
  el.pause();
  el.removeAttribute('src');
  el.load();
  const release = releaseHandlers.get(previous);
  if (release) release();
  return el;
};

/**
 * Point the element at `src`, restoring `resumeAt` once metadata has landed.
 * `currentTime` is silently ignored before that, which is how a re-minted URL
 * used to throw the listener back to the start of the call.
 */
const applySource = (el, src, resumeAt) => {
  el.src = src;
  if (!(resumeAt > 0)) return;
  const seek = () => {
    el.removeEventListener('loadedmetadata', seek);
    el.currentTime = Math.min(resumeAt, Number.isFinite(el.duration) ? el.duration : resumeAt);
  };
  el.addEventListener('loadedmetadata', seek);
};

const isFiniteNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * @param {object} props
 * @param {string} props.callId
 * @param {boolean|null} [props.initialHasAudio] what the caller already knows
 *   from the call row, so the drawer does not spend a round trip re-asking.
 *   `null` means "unknown, go and check".
 */
const AudioPlayer = ({ callId, className = '', initialHasAudio = null }) => {
  const tokenRef = useRef(null);
  if (tokenRef.current === null) tokenRef.current = {};

  const [hasAudio, setHasAudio] = useState(initialHasAudio);
  const [checking, setChecking] = useState(initialHasAudio === null);
  const [url, setUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [error, setError] = useState(null);
  const [owns, setOwns] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [total, setTotal] = useState(null);
  const [rate, setRate] = useState(1);

  const mintedAtRef = useRef(0);
  // The play/seek paths are imperative and must read the current speed without
  // re-creating themselves every time the speed changes.
  const rateRef = useRef(1);
  useEffect(() => {
    rateRef.current = rate;
  }, [rate]);

  /* Availability. Reset first: every piece of state below describes the previous
     call's recording, and a stale duration under a new call's name is a lie. */
  useEffect(() => {
    setUrl(null);
    mintedAtRef.current = 0;
    setError(null);
    setPosition(0);
    setTotal(null);
    setPlaying(false);
    setOwns(false);
    if (sharedOwner === tokenRef.current) stopShared();

    if (!callId) {
      setHasAudio(false);
      setChecking(false);
      return undefined;
    }
    if (initialHasAudio !== null) {
      setHasAudio(initialHasAudio);
      setChecking(false);
      return undefined;
    }
    if (audioAvailabilityCache.has(callId)) {
      setHasAudio(audioAvailabilityCache.get(callId));
      setChecking(false);
      return undefined;
    }

    // `cancelled` is the fix for the original: prev/next through a queue fires
    // one of these per call, and the slowest response used to win.
    let cancelled = false;
    setChecking(true);
    authenticatedFetch(`/api/audio/info/${encodeURIComponent(callId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const available = Boolean(data && data.success && data.has_audio);
        setHasAudio(available);
        audioAvailabilityCache.set(callId, available);
      })
      .catch(() => {
        if (cancelled) return;
        // A failed probe is not a confirmed absence, so it is not cached — the
        // next open should ask again rather than inherit a network blip.
        setHasAudio(false);
        setError('Could not check whether this call was recorded.');
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [callId, initialHasAudio]);

  /** Mint a signed URL. Resolves to the URL, or null once the error is shown. */
  const fetchUrl = useCallback(async () => {
    if (!callId) return null;
    setLoadingUrl(true);
    setError(null);
    try {
      const res = await authenticatedFetch(
        `/api/audio/signed-url/${encodeURIComponent(callId)}?expiration_minutes=${URL_TTL_MINUTES}`,
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.signed_url) {
        throw new Error((data && data.detail) || 'The recording could not be opened.');
      }
      setUrl(data.signed_url);
      mintedAtRef.current = Date.now();
      return data.signed_url;
    } catch (err) {
      setError(err.message || 'The recording could not be opened.');
      return null;
    } finally {
      setLoadingUrl(false);
    }
  }, [callId]);

  /* Resolve the URL up front. Doing this at open rather than on the click keeps
     the click inside the browser's user-gesture window: an `await` between the
     click and `play()` is what Safari rejects as "not user initiated". */
  useEffect(() => {
    if (hasAudio !== true || url || loadingUrl || error) return;
    fetchUrl();
  }, [hasAudio, url, loadingUrl, error, fetchUrl]);

  /* Take the idle element so the duration and the scrub bar are live before the
     first click. Only when nothing owns it — stealing it here would silence a
     recording the user is already listening to somewhere else. */
  useEffect(() => {
    if (!url || owns || sharedOwner !== null) return;
    const el = claimShared(tokenRef.current);
    setOwns(true);
    applySource(el, url, 0);
    el.playbackRate = rateRef.current;
    // No play() here, and none anywhere outside an event handler.
  }, [url, owns]);

  /* Transport state, mirrored from the element only while we hold it. */
  useEffect(() => {
    if (!owns) return undefined;
    const el = sharedAudio();

    const onTime = () => setPosition(el.currentTime);
    const onMeta = () => setTotal(isFiniteNum(el.duration) && el.duration > 0 ? el.duration : null);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setPosition(0);
    };
    const onRate = () => setRate(el.playbackRate);
    const onError = () => {
      // Losing the element to another player empties it, and an empty element is
      // allowed to raise an error event. That is a handover, not a failure, so it
      // must not leave "the recording could not load" on a control the user is
      // no longer looking at.
      if (!el.currentSrc && !el.getAttribute('src')) return;
      setPlaying(false);
      // Almost always an expired signature. Clearing the URL lets Retry re-mint.
      setUrl(null);
      mintedAtRef.current = 0;
      setError('The recording stopped loading. Its access link may have expired.');
    };

    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('play', onPlay);
    el.addEventListener('playing', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('ratechange', onRate);
    el.addEventListener('error', onError);

    // The element may already be past loadedmetadata by the time this effect
    // runs, so read it once instead of waiting for an event that has been.
    onMeta();
    onTime();
    setPlaying(!el.paused);

    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('playing', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('ratechange', onRate);
      el.removeEventListener('error', onError);
    };
  }, [owns]);

  /* Membership of the shared element. Registered once per instance, not per
     render, so the token in the map is the token in the closure. */
  useEffect(() => {
    const token = tokenRef.current;
    releaseHandlers.set(token, () => {
      setOwns(false);
      setPlaying(false);
      setPosition(0);
    });
    return () => {
      releaseHandlers.delete(token);
      // Unmounting while playing must stop the audio: a voice with no visible
      // transport control is the worst state this component can leave behind.
      if (sharedOwner === token) stopShared();
    };
  }, []);

  /** Bind the element to this player and this call's audio, minting if needed. */
  const bind = useCallback(async () => {
    let src = url;
    if (!src || Date.now() - mintedAtRef.current > URL_STALE_MS) {
      src = await fetchUrl();
      if (!src) return null;
    }
    const held = sharedOwner === tokenRef.current;
    const resumeAt = held ? sharedAudio().currentTime : 0;
    const el = claimShared(tokenRef.current);
    setOwns(true);
    if (el.src !== src) applySource(el, src, resumeAt);
    el.playbackRate = rateRef.current;
    return el;
  }, [url, fetchUrl]);

  const togglePlay = useCallback(async () => {
    if (owns && !sharedAudio().paused) {
      sharedAudio().pause();
      return;
    }
    const el = await bind();
    if (!el) return;
    try {
      await el.play();
    } catch {
      // Autoplay policy, or the gesture window closed while the URL was minted.
      setError('Playback did not start. Press play again.');
    }
  }, [owns, bind]);

  const seekTo = useCallback(
    async (seconds) => {
      const el = owns ? sharedAudio() : await bind();
      if (!el) return;
      const ceiling = isFiniteNum(el.duration) ? el.duration : total;
      const next = Math.max(0, isFiniteNum(ceiling) ? Math.min(seconds, ceiling) : seconds);
      el.currentTime = next;
      // Mirror immediately: `timeupdate` can lag a drag by a frame or two, and a
      // scrub bar that snaps back under the cursor feels broken.
      setPosition(next);
    },
    [owns, bind, total],
  );

  const changeRate = useCallback(
    (next) => {
      setRate(next);
      rateRef.current = next;
      if (sharedOwner === tokenRef.current) sharedAudio().playbackRate = next;
    },
    [],
  );

  /**
   * Keys are handled at the region level, so they work while focus is on any of
   * the transport controls. Anything that owns the key natively is left alone:
   * the scrub bar's own arrows step it, and Space on a focused button clicks it.
   */
  const onKeyDown = (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

    if (event.key === ' ' || event.key === 'Spacebar') {
      if (target && typeof target.closest === 'function' && target.closest('button')) return;
      event.preventDefault();
      togglePlay();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      seekTo(position - SKIP_SECONDS);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      seekTo(position + SKIP_SECONDS);
    }
  };

  const shell = `rounded-lg border border-line bg-surface p-card ${className}`;

  if (checking) {
    return (
      <div className={shell} role="status">
        <div className="flex items-center gap-2 text-label text-ink-500">
          <Spinner size={14} />
          Checking for a recording
        </div>
      </div>
    );
  }

  if (hasAudio !== true) {
    return (
      <div className={shell}>
        <div className="flex items-center gap-2 text-label text-ink-500">
          <i className="fas fa-volume-xmark text-ink-400" aria-hidden="true" />
          {/* Stated as a fact about the call, not as an error: most tenants
              record only a subset of their lines, and that is normal. */}
          {error || 'No recording was stored for this call.'}
        </div>
      </div>
    );
  }

  const scrubMax = isFiniteNum(total) ? total : 0;
  const scrubbable = scrubMax > 0;
  const elapsed = fmtDuration(position);
  const length = isFiniteNum(total) ? fmtDuration(total) : EMPTY;

  return (
    // A region rather than a bare div: it is a landmark a screen-reader user
    // jumps to, and the keydown handler needs a single home.
    <section
      className={shell}
      aria-label="Call recording"
      onKeyDown={onKeyDown}
    >
      <div className="mb-gutter flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-label font-medium text-ink-900">
          <i className="fas fa-headphones text-ink-500" aria-hidden="true" />
          Recording
          {loadingUrl ? <Spinner size={12} className="text-ink-500" /> : null}
        </div>
        <SegmentedControl
          value={rate}
          onChange={changeRate}
          options={SPEEDS}
          label="Playback speed"
        />
      </div>

      {error ? (
        <div className="mb-gutter flex flex-wrap items-center gap-2 rounded-md border border-bad-line bg-bad-tint px-2 py-1.5 text-label text-bad-text">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          <span className="min-w-0 flex-1">{error}</span>
          <Button size="sm" iconLeft="fa-rotate-right" onClick={fetchUrl} loading={loadingUrl}>
            Retry
          </Button>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <IconButton
          icon="fa-rotate-left"
          label={`Back ${SKIP_SECONDS} seconds`}
          onClick={() => seekTo(position - SKIP_SECONDS)}
          disabled={!scrubbable}
        />
        <IconButton
          icon={playing ? 'fa-pause' : 'fa-play'}
          label={playing ? 'Pause' : 'Play'}
          variant="primary"
          size="lg"
          onClick={togglePlay}
          loading={loadingUrl && !url}
        />
        <IconButton
          icon="fa-rotate-right"
          label={`Forward ${SKIP_SECONDS} seconds`}
          onClick={() => seekTo(position + SKIP_SECONDS)}
          disabled={!scrubbable}
        />

        <span className="shrink-0 font-mono text-micro tabular-nums text-ink-700">
          {elapsed}
          <span className="text-ink-500"> / {length}</span>
        </span>

        {/* A native range: it is the only scrub bar that arrives with keyboard
            stepping, Home/End, and a real thumb on every platform. `accent-brand`
            themes it without an appearance reset that would erase the thumb. */}
        <input
          type="range"
          className="min-w-0 flex-1 accent-brand"
          min={0}
          max={scrubbable ? scrubMax : 1}
          step={0.5}
          value={Math.min(position, scrubbable ? scrubMax : 1)}
          disabled={!scrubbable}
          aria-label="Position in the recording"
          aria-valuetext={`${elapsed} of ${length}`}
          onChange={(event) => seekTo(Number(event.target.value))}
        />
      </div>

      <p className="mt-2 text-micro text-ink-500">
        Space plays and pauses · arrow keys move {SKIP_SECONDS} seconds
      </p>
    </section>
  );
};

export default AudioPlayer;
